import { addDays, addYears, parseISO } from "date-fns";
import { z } from "zod";
import { isoDateSchema, toZonedDateOnly } from "@common/helpers/date";

/**
 * Who a timeline slice credits. People includes agent runs (counted under
 * the person who started them); Kick includes Kick Support.
 */
export enum ActivitySeries {
    PEOPLE = "people",
    KICK = "kick",
    INTEGRATIONS = "integrations",
    RULES = "rules",
}

/** One timeline bar: an hour, a day, a week starting on Monday, or a calendar month. */
export enum HistoryCountsBucket {
    HOUR = "hour",
    DAY = "day",
    WEEK = "week",
    MONTH = "month",
}

/** An hour bucket's key: its local start, `YYYY-MM-DDTHH:00`, sorting with day keys. */
export const isoLocalHourSchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}T([01]\d|2[0-3]):00$/)
    .refine((value) => isoDateSchema.safeParse(value.slice(0, 10)).success, {
        message: "Invalid calendar date",
    });

/** A counts row's bucket: a day (`YYYY-MM-DD`), a week's Monday, a month's 1st, or an hour (`YYYY-MM-DDTHH:00`). */
export const historyCountsBucketKeySchema = z.union([
    isoDateSchema,
    isoLocalHourSchema,
]);

export enum HistoryCountsBreakdown {
    SERIES = "series",
    /** `series` plus People split by person: the member, or whoever started an agent run. */
    SERIES_AND_PEOPLE = "seriesAndPeople",
}

export const MAX_HOURLY_COUNTS_DAYS = 29;
const MAX_DAILY_COUNTS_DAYS = 400;
const MAX_COUNTS_YEARS = 10;

interface HistoryCountsRangeQuery {
    filters?: {
        minCreatedAt?: Date;
        maxCreatedAt?: Date;
        outcome?: string;
        series?: readonly string[];
    };
    bucket?: HistoryCountsBucket;
    timeZone?: string;
    breakdown?: HistoryCountsBreakdown;
}

/**
 * Caps grouped counts queries: with `bucket`, `breakdown` or `timeZone` the
 * range needs both ends, spans at most 29 days in hourly buckets, 400 days in
 * daily buckets and 10 years in any, counted in days of `timeZone`. Counts without any stay unbounded.
 * An outcome or series filter is capped the same way.
 */
export function refineHistoryCountsRange(
    { filters, bucket, timeZone, breakdown }: HistoryCountsRangeQuery,
    ctx: z.RefinementCtx,
): void {
    const isClassified = !!filters?.outcome || !!filters?.series?.length;
    if (!bucket && !breakdown && !timeZone && !isClassified) {
        return;
    }
    if (!filters?.minCreatedAt || !filters.maxCreatedAt) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["filters"],
            message: "Pick a start and end date for timeline counts",
        });
        return;
    }
    const toDay = (date: Date) => parseISO(toZonedDateOnly({ date, timeZone }));
    const startDay = toDay(filters.minCreatedAt);
    const endDay = toDay(filters.maxCreatedAt);
    const isDaily =
        (bucket ?? HistoryCountsBucket.DAY) === HistoryCountsBucket.DAY;
    if (
        bucket === HistoryCountsBucket.HOUR &&
        endDay >= addDays(startDay, MAX_HOURLY_COUNTS_DAYS)
    ) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["filters", "maxCreatedAt"],
            message: `Pick a range of ${MAX_HOURLY_COUNTS_DAYS} days or less for hourly counts`,
        });
    } else if (isDaily && endDay >= addDays(startDay, MAX_DAILY_COUNTS_DAYS)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["filters", "maxCreatedAt"],
            message: `Pick a range of ${MAX_DAILY_COUNTS_DAYS} days or less for daily counts`,
        });
    } else if (endDay >= addYears(startDay, MAX_COUNTS_YEARS)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["filters", "maxCreatedAt"],
            message: `Pick a range of ${MAX_COUNTS_YEARS} years or less for timeline counts`,
        });
    }
}
